import React, { useEffect, useState } from 'react'
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { message, DatePicker } from 'antd';
import ProfileNavigateLayout from './ProfileNavigateLayout';
import { useAuth } from "../../context/AuthContext";
import { UserContext } from "../../context/UserContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NAME_REGEX = /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+(?:[-\s][a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+)*$/;
const PHONENUMBER_REGEX = /^(\+48[-\s.]?)?(\d{3}[-\s.]?\d{3}[-\s.]?\d{3})$/;

const ProfileSettings = () => {

    const { user } = useAuth(UserContext);
    const { register, handleSubmit, getValues, setValue } = useForm();
    const [date, setDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [initialData, setInitialData] = useState({});
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [isPhoneNumberChecking, setIsPhoneNumberChecking] = useState(false);

    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phoneNumber: "",
      });
    
      const validateForm = async (formData) => {
        let newErrors = {};
        let isValid = true;

        const phoneNumber = formData?.phoneNumber || getValues("phoneNumber");
        const email = user.email;
    
        if (!NAME_REGEX.test(formData.firstName)) {
            newErrors.firstName = "Imię nie może zawierać cyfr ani znaków specjalnych";
            isValid = false;
        }
    
        if (!NAME_REGEX.test(formData.lastName)) {
            newErrors.lastName = "Nazwisko nie może zawierać cyfr ani znaków specjalnych";
            isValid = false;
        }
    
        if (!NAME_REGEX.test(formData.city)) {
            newErrors.city = "Miasto nie może zawierać cyfr ani znaków specjalnych";
            isValid = false;
        }
    
        if (!PHONENUMBER_REGEX.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "Numer telefonu nie ma poprawnego formatu";
            isValid = false;
        } else {
            setIsPhoneNumberChecking(true);
            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/user/checkPhoneNumber?phoneNumber=${encodeURIComponent(phoneNumber)}`,
                );
                if (response.ok) {
                    const users = await response.json();
                    const phoneNumberDoesNotExist = users.some(
                        (user) => user.phoneNumber === phoneNumber,
                    );
                    if (phoneNumberDoesNotExist) {
                        newErrors.phoneNumber =
                            "Konto z podanym numerem telefonu już istnieje.";
                        isValid = false;
                    }
                } else {
                    console.error("Błąd przy sprawdzaniu numeru telefonu");
                }
            } catch (error) {
                console.error(
                    "Wystąpił błąd podczas sprawdzania numeru telefonu:",
                    error,
                );
            } finally {
                setIsPhoneNumberChecking(false);
            }
        }
    
        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        if (user) {
            const userData = {
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                phoneNumber: user.phoneNumber || "",
                country: user.country || "",
                state: user.state || "",
                city: user.city || "",
                zipCode: user.zipCode || "",
                street: user.street || "",
                addressNo1: user.addressNo1 || "",
                addressNo2: user.addressNo2 || "",
                dateOfBirth: user.dateOfBirth ? moment(user.dateOfBirth).format("YYYY-MM-DD") : null,
            };
            setInitialData(userData);
            Object.keys(userData).forEach(key => setValue(key, userData[key]));
            setDate(userData.dateOfBirth ? moment(userData.dateOfBirth) : null);
        }
    }, [user, setValue]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setIsFormChanged(value !== initialData[name]);
        setValue(name, value);
    };
    
    const onChangeDate = (dateString) => {
        setDate(dateString);
        setIsFormChanged(dateString !== initialData.dateOfBirth);
    };

    const onSubmit = async (formData) => {
        setIsLoading(true);

        const isValid = await validateForm(formData);
        if (!isValid) {
            setIsLoading(false);
            return;
        }

        const updatedFields = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== initialData[key]) {
                updatedFields[key] = formData[key];
            }
        });

        if (date !== initialData.dateOfBirth) {
            updatedFields.dateOfBirth = date;
        }
    
        if (Object.keys(updatedFields).length === 0) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/user/${user.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify(updatedFields),
            });

            if (response.ok) {
                message.success("Profil zaktualizowany!");
                setInitialData({ ...initialData, ...updatedFields });
                setIsFormChanged(false);
            } else {
                message.error("Błąd podczas zapisu zmian.");
            }
        } catch (error) {
            console.error("Błąd:", error);
            message.error("Wystąpił błąd!");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ProfileNavigateLayout>
        <div style={{ marginBottom: '10rem' }}>
            <div className="w-100 mb-3 rounded mb-5 p-2" style={{ background: '#f8f9fa' }}>
                <h5 className="text-title-h5 mb-2 mt-3">Twoje Dane</h5>
                <form className="row form-row" onSubmit={handleSubmit(onSubmit)}>
                    <div className="col-md-12">
                        <div className="form-group">
                            <div className='change-avatar d-flex gap-2 align-items-center'>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Imię</label>
                            <input defaultValue={user?.firstName} {...register("firstName")} className="form-control" onChange={handleInputChange} />
                            </div>
                            {errors.firstName && (
                                <small className="error-text">{errors.firstName}</small>
                            )}
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Nazwisko</label>
                            <input defaultValue={user?.lastName} {...register("lastName")} className="form-control" onChange={handleInputChange} />
                            </div>
                            {errors.lastName && (
                                <small className="error-text">{errors.lastName}</small>
                            )}                            
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Email</label>
                            <input defaultValue={user?.email} disabled className="form-control" />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Numer Telefonu</label>
                            <input defaultValue={user?.phoneNumber} {...register("phoneNumber")} className="form-control" onChange={handleInputChange} />
                        </div>
                        {errors.phoneNumber && (
                            <small className="error-text">{errors.phoneNumber}</small>
                        )}                            
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label style={{zIndex: 10, position: "relative"}}>Data Urodzenia</label>
                            <DatePicker onChange={onChangeDate} value={date ? moment(date) : null} format={"YYYY-MM-DD"} className="date-picker"
                                placeholder="Wybierz Datę"/>
                        </div>
                    </div>

                    {/* <div className="col-md-6">
                        <div className="form-group mb-2">
                            <label>Płeć</label>
                            <select className="form-control select" onChange={handleChange} name='gender'>
                                <option value={''}>Wybierz</option>
                                <option className='text-capitalize'>Mężczyzna</option>
                                <option className='text-capitalize'>Kobieta</option>
                                <option className='text-capitalize'>Inne</option>
                            </select>
                        </div>
                    </div> */}

                    <h5 className="text-title-h5 mb-2 mt-3">Adres</h5>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Państwo</label>
                            <input defaultValue={user?.country} {...register("state")} className="form-control" onChange={handleInputChange} />

                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Województwo</label>
                            <input defaultValue={user?.state} {...register("state")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Miasto</label>
                            <input defaultValue={user?.city} {...register("city")} className="form-control" onChange={handleInputChange} />
                            </div>
                    </div>        
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Kod Pocztowy</label>
                            <input defaultValue={user?.zipCode} {...register("zipCode")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Ulica</label>
                            <input defaultValue={user?.street} {...register("street")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Numer domu/lokalu</label>
                            <input defaultValue={user?.address1} {...register("addressNo1")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className='text-center'>
                        <button type="submit" 
                            className={`btn btn-primary btn-block mt-2 iBtn ${isFormChanged ? 'iBtn-dark' : ''}`} 
                            disabled={!isFormChanged || isLoading}
                        >
                            {isLoading ? 'Ładowanie..' : 'Zapisz Zmiany'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </ProfileNavigateLayout>
    )
}

export default ProfileSettings;